import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { srnAPI, purchaseOrderAPI, userAPI, servicePOAPI, getBillUrl, downloadBill, viewBill } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { DataTable } from '../components/DataTable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Briefcase, 
  Loader2, 
  Eye,
  Plus,
  Trash2,
  Pencil,
  Receipt,
  RefreshCw,
  Merge,
  Search,
  Calculator,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../lib/utils';

const ServiceReceivedPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preSelectedPOId = searchParams.get('po_id');
  
  const [srns, setSrns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [servicePOs, setServicePOs] = useState([]);
  const [allServicePOs, setAllServicePOs] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedSRN, setSelectedSRN] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Merge selection states
  const [selectedSRNs, setSelectedSRNs] = useState([]);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  
  // Recalculate states
  const [recalculating, setRecalculating] = useState(null);
  
  // Filter states
  const [filterVendor, setFilterVendor] = useState('all');
  const [filterPO, setFilterPO] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    po_id: '',
    received_by: '',
    receipt_date: '',
    services: [],
    notes: '',
    bill_number: '',
    bill_url: '',
    gst_entries: []
  });
  
  // Current service item being added
  const [currentService, setCurrentService] = useState({
    description: '',
    quantity: 1,
    unit: '',
    rate: ''
  });
  
  // Current GST entry being added
  const [currentGST, setCurrentGST] = useState({
    percentage: 18,
    amount: ''
  });

  // Super Admin check
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Check if user is Super Admin
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setIsSuperAdmin(user?.role_name === 'Super Admin');
      }
    } catch (e) {
      console.error('Error parsing user:', e);
    }
  }, []);

  // Handle pre-selected PO from URL
  useEffect(() => {
    if (preSelectedPOId && allServicePOs.length > 0 && !dialogOpen) {
      const po = allServicePOs.find(p => p.id === preSelectedPOId);
      if (po) {
        handleOpenDialog(null, preSelectedPOId);
        populateServicesFromPO(preSelectedPOId);
        // Clear the URL parameter after opening dialog
        navigate('/srn', { replace: true });
      }
    }
  }, [preSelectedPOId, allServicePOs]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [srnsRes, posRes, usersRes] = await Promise.all([
        srnAPI.getAll(),
        purchaseOrderAPI.getAll(),
        userAPI.getAll()
      ]);
      
      setSrns(srnsRes.data);
      // Filter only service POs
      const servicePOsData = posRes.data.filter(po => po.po_type === 'service');
      setAllServicePOs(servicePOsData);
      // Filter only approved/open service POs for new SRN
      setServicePOs(servicePOsData.filter(po => ['approved', 'open'].includes(po.status)));
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get unique vendors from SRNs
  const uniqueVendors = [...new Set(srns.map(s => s.vendor_name || s.custom_vendor_name).filter(Boolean))];
  
  // Filter SRNs
  const filteredSRNs = srns.filter(srn => {
    const matchesVendor = filterVendor === 'all' || 
      (srn.vendor_name === filterVendor || srn.custom_vendor_name === filterVendor);
    const matchesPO = filterPO === 'all' || srn.po_id === filterPO;
    const matchesSearch = !searchQuery || 
      srn.srn_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      srn.po_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      srn.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesVendor && matchesPO && matchesSearch;
  });

  // Pull the PO's service items into the SRN form
  const populateServicesFromPO = async (poId) => {
    if (!poId) return;
    try {
      const res = await purchaseOrderAPI.getById(poId);
      const services = (res.data?.items || []).map(it => ({
        description: it.description || it.item_name || '',
        quantity: it.quantity || 1,
        unit: it.unit || it.unit_name || '',
        rate: it.rate || 0
      }));
      if (services.length > 0) {
        setFormData(prev => ({ ...prev, services }));
        toast.success(`${services.length} service item(s) loaded from the PO`);
      }
    } catch (e) {
      // keep form as-is on failure
    }
  };

  const handleOpenDialog = (srn = null, preselectedPOId = null) => {
    if (srn) {
      setSelectedSRN(srn);
      setFormData({
        po_id: srn.po_id || '',
        received_by: srn.received_by || '',
        receipt_date: srn.receipt_date?.split('T')[0] || '',
        services: srn.services || [],
        notes: srn.notes || '',
        bill_number: srn.bill_number || '',
        bill_url: srn.bill_url || '',
        gst_entries: srn.gst_entries || []
      });
    } else {
      setSelectedSRN(null);
      setFormData({
        po_id: preselectedPOId || '',
        received_by: '',
        receipt_date: new Date().toISOString().split('T')[0],
        services: [],
        notes: '',
        bill_number: '',
        bill_url: '',
        gst_entries: []
      });
    }
    setCurrentService({ description: '', quantity: 1, unit: '', rate: '' });
    setCurrentGST({ percentage: 18, amount: '' });
    setDialogOpen(true);
  };

  const handleViewSRN = async (srn) => {
    try {
      const response = await srnAPI.getById(srn.id);
      setSelectedSRN(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load SRN details');
    }
  };

  const handleAddService = () => {
    if (!currentService.description || !currentService.rate) {
      toast.error('Please enter service description and rate');
      return;
    }
    
    const amount = parseFloat(currentService.quantity) * parseFloat(currentService.rate);
    setFormData({
      ...formData,
      services: [...formData.services, { ...currentService, rate: parseFloat(currentService.rate), amount }]
    });
    setCurrentService({ description: '', quantity: 1, unit: '', rate: '' });
  };

  const handleRemoveService = (index) => {
    setFormData({
      ...formData,
      services: formData.services.filter((_, i) => i !== index)
    });
  };

  const handleAddGST = () => {
    if (!currentGST.amount || parseFloat(currentGST.amount) <= 0) {
      toast.error('Please enter a valid GST amount');
      return;
    }
    
    setFormData({
      ...formData,
      gst_entries: [...formData.gst_entries, { 
        percentage: currentGST.percentage, 
        amount: parseFloat(currentGST.amount) 
      }]
    });
    setCurrentGST({ percentage: 18, amount: '' });
  };

  const handleRemoveGST = (index) => {
    setFormData({
      ...formData,
      gst_entries: formData.gst_entries.filter((_, i) => i !== index)
    });
  };

  const calculateTotals = () => {
    const serviceTotal = formData.services.reduce((sum, s) => sum + (s.amount || 0), 0);
    const gstTotal = formData.gst_entries.reduce((sum, g) => sum + (g.amount || 0), 0);
    const gst18 = formData.gst_entries.filter(g => g.percentage === 18).reduce((sum, g) => sum + g.amount, 0);
    return { serviceTotal, gstTotal, grandTotal: serviceTotal + gstTotal, inputCredit: gst18 };
  };

  const handleSave = async () => {
    if (!formData.po_id) {
      toast.error('Please select a Purchase Order');
      return;
    }
    if (!formData.received_by) {
      toast.error('Please select who received the service');
      return;
    }
    if (formData.services.length === 0) {
      toast.error('Please add at least one service');
      return;
    }

    try {
      setSaving(true);
      
      if (selectedSRN) {
        await srnAPI.update(selectedSRN.id, formData);
        toast.success('Service Received Note updated');
      } else {
        await srnAPI.create(formData);
        toast.success('Service Received Note created');
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SRN');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (srn) => {
    if (!window.confirm(`Are you sure you want to delete ${srn.srn_number}?`)) {
      return;
    }
    
    try {
      await srnAPI.delete(srn.id);
      toast.success('SRN deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete SRN');
    }
  };

  // Merge SRNs handlers
  const handleSelectSRN = (srnId, checked) => {
    if (checked) {
      setSelectedSRNs([...selectedSRNs, srnId]);
    } else {
      setSelectedSRNs(selectedSRNs.filter(id => id !== srnId));
    }
  };

  const handleSelectAllSRNs = (checked) => {
    if (checked) {
      setSelectedSRNs(filteredSRNs.map(s => s.id));
    } else {
      setSelectedSRNs([]);
    }
  };

  const canMerge = () => {
    if (selectedSRNs.length < 2) return { canMerge: false, reason: 'Select at least 2 SRNs to merge' };
    
    const selectedSRNsData = srns.filter(s => selectedSRNs.includes(s.id));
    const poIds = [...new Set(selectedSRNsData.map(s => s.po_id))];
    
    if (poIds.length > 1) {
      return { canMerge: false, reason: 'Selected SRNs must belong to the same Purchase Order' };
    }
    
    return { canMerge: true, reason: '' };
  };

  const handleMergeSRNs = async () => {
    const { canMerge: canDoMerge, reason } = canMerge();
    if (!canDoMerge) {
      toast.error(reason);
      return;
    }
    
    try {
      setMerging(true);
      await srnAPI.merge(selectedSRNs);
      toast.success('SRNs merged successfully');
      setMergeDialogOpen(false);
      setSelectedSRNs([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge SRNs');
    } finally {
      setMerging(false);
    }
  };

  // Recalculate balance for the PO linked to this SRN
  const handleRecalculate = async (srn) => {
    try {
      setRecalculating(srn.id);
      await servicePOAPI.recalculateBalance(srn.po_id);
      toast.success('PO balance recalculated successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recalculate balance');
    } finally {
      setRecalculating(null);
    }
  };

  const totals = calculateTotals();
  const mergeStatus = canMerge();

  return (
    <div className="space-y-6" data-testid="srn-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Briefcase className="h-6 w-6 md:h-7 md:w-7" />
            Service Received Notes (SRN)
          </h1>
          <p className="text-muted-foreground">Track services received against service purchase orders</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="create-srn-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create SRN
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search SRN#, PO#, or vendor..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterVendor} onValueChange={setFilterVendor}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="All Vendors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vendors</SelectItem>
                {uniqueVendors.map(vendor => (
                  <SelectItem key={vendor} value={vendor}>{vendor}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterPO} onValueChange={setFilterPO}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="All POs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All POs</SelectItem>
                {allServicePOs.map(po => (
                  <SelectItem key={po.id} value={po.id}>{po.po_number}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Merge Selection Bar */}
      {selectedSRNs.length > 0 && (
        <Card className="border-primary">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{selectedSRNs.length} selected</Badge>
                <span className="text-sm text-muted-foreground">Service Received Notes</span>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedSRNs([])}
                >
                  Clear Selection
                </Button>
                <Button 
                  size="sm"
                  onClick={() => setMergeDialogOpen(true)}
                  disabled={!mergeStatus.canMerge}
                  title={mergeStatus.reason || 'Merge selected SRNs'}
                  data-testid="merge-srns-btn"
                >
                  <Merge className="h-4 w-4 mr-2" />
                  Merge SRNs
                </Button>
              </div>
            </div>
            {!mergeStatus.canMerge && selectedSRNs.length >= 2 && (
              <p className="text-xs text-amber-600 mt-2">{mergeStatus.reason}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* SRN List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Service Received Notes ({filteredSRNs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            module="srn"
            data={filteredSRNs}
            loading={loading}
            title="Service Received Notes Report"
            columns={[
              {
                key: 'select',
                label: (
                  <Checkbox
                    checked={selectedSRNs.length === filteredSRNs.length && filteredSRNs.length > 0}
                    onCheckedChange={handleSelectAllSRNs}
                    data-testid="select-all-srns"
                  />
                ),
                sortable: false,
                width: '40px',
                render: (_, srn) => (
                  <Checkbox
                    checked={selectedSRNs.includes(srn.id)}
                    onCheckedChange={(checked) => handleSelectSRN(srn.id, checked)}
                    data-testid={`select-srn-${srn.id}`}
                  />
                )
              },
              {
                key: 'srn_number',
                label: 'SRN #',
                sortable: true,
                render: (value) => <span className="font-medium font-mono">{value}</span>
              },
              {
                key: 'po_number',
                label: 'PO #',
                sortable: true,
                render: (value) => <span className="font-mono text-sm">{value}</span>
              },
              {
                key: 'vendor_name',
                label: 'Vendor',
                sortable: true,
                render: (_, srn) => srn.vendor_name || srn.custom_vendor_name || '-'
              },
              {
                key: 'receipt_date',
                label: 'Date',
                sortable: true,
                render: (value) => formatDate(value)
              },
              {
                key: 'services',
                label: 'Services',
                render: (_, srn) => (
                  <Badge variant="secondary">{srn.services?.length || 0}</Badge>
                )
              },
              {
                key: 'total_value',
                label: 'Total',
                sortable: true,
                render: (value) => formatCurrency(value)
              },
              {
                key: 'total_gst',
                label: 'GST',
                sortable: true,
                render: (value, srn) => value > 0 ? (
                  <div className="flex flex-col">
                    <span className="text-green-600">{formatCurrency(value)}</span>
                    {srn.input_credit_eligible > 0 && (
                      <span className="text-xs text-green-500">ITC: {formatCurrency(srn.input_credit_eligible)}</span>
                    )}
                  </div>
                ) : '-'
              },
              {
                key: 'grand_total',
                label: 'Grand Total',
                sortable: true,
                render: (_, srn) => (
                  <span className="font-medium">{formatCurrency(srn.grand_total || srn.total_value)}</span>
                )
              }
            ]}
            renderActions={(srn) => (
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleViewSRN(srn)}
                  title="View Details"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenDialog(srn)}
                  title="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                {isSuperAdmin && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRecalculate(srn)}
                      disabled={recalculating === srn.id}
                      title="Recalculate PO Balance"
                      className="text-orange-600"
                    >
                      {recalculating === srn.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Calculator className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(srn)}
                      title="Delete (Super Admin)"
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            )}
          />
        </CardContent>
      </Card>

      {/* Create/Edit SRN Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[1100px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {selectedSRN ? 'Edit Service Received Note' : 'Create Service Received Note'}
            </DialogTitle>
            <DialogDescription>
              Record services received from a vendor. No stock will be updated.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Service PO *</Label>
                <Select
                  value={formData.po_id}
                  onValueChange={(value) => {
                    setFormData(prev => ({ ...prev, po_id: value }));
                    populateServicesFromPO(value);
                  }}
                  disabled={!!selectedSRN}
                >
                  <SelectTrigger data-testid="srn-po-select">
                    <SelectValue placeholder="Select Service PO" />
                  </SelectTrigger>
                  <SelectContent>
                    {servicePOs.map(po => (
                      <SelectItem key={po.id} value={po.id}>
                        {po.po_number} - {po.vendor_name || po.custom_vendor_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {servicePOs.length === 0 && (
                  <p className="text-xs text-amber-600">No approved Service POs available</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Received By *</Label>
                <Select
                  value={formData.received_by}
                  onValueChange={(value) => setFormData({ ...formData, received_by: value })}
                >
                  <SelectTrigger data-testid="srn-received-by-select">
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Receipt Date</Label>
                <Input
                  type="date"
                  value={formData.receipt_date}
                  onChange={(e) => setFormData({ ...formData, receipt_date: e.target.value })}
                  data-testid="srn-receipt-date"
                />
              </div>
            </div>

            {/* Bill Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bill Number</Label>
                <Input
                  value={formData.bill_number}
                  onChange={(e) => setFormData({ ...formData, bill_number: e.target.value })}
                  placeholder="Enter bill/invoice number"
                  data-testid="srn-bill-number"
                />
              </div>
              <div className="space-y-2">
                <Label>Bill URL</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.bill_url}
                    onChange={(e) => setFormData({ ...formData, bill_url: e.target.value })}
                    placeholder="Upload or enter bill URL"
                    data-testid="srn-bill-url"
                    className="flex-1"
                  />
                  {formData.bill_url && (
                    <>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="icon"
                        onClick={() => viewBill(formData.bill_url)}
                        title="View Bill"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="icon"
                        onClick={() => downloadBill(formData.bill_url, `bill_${formData.bill_number || 'srn'}`)}
                        title="Download Bill"
                        className="text-green-600"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Services */}
            <div className="space-y-4">
              <h4 className="font-medium">Services Received</h4>
              
              {/* Add Service Form */}
              <div className="grid grid-cols-12 gap-2 p-3 bg-muted rounded-lg">
                <div className="col-span-12 md:col-span-5">
                  <Input
                    placeholder="Service description"
                    value={currentService.description}
                    onChange={(e) => setCurrentService({ ...currentService, description: e.target.value })}
                    data-testid="srn-service-description"
                  />
                </div>
                <div className="col-span-4 md:col-span-2">
                  <Input
                    type="number"
                    placeholder="Qty"
                    min="0.01"
                    step="0.01"
                    value={currentService.quantity}
                    onChange={(e) => setCurrentService({ ...currentService, quantity: e.target.value })}
                    data-testid="srn-service-qty"
                  />
                </div>
                <div className="col-span-4 md:col-span-2">
                  <Input
                    placeholder="Unit (hrs, days)"
                    value={currentService.unit}
                    onChange={(e) => setCurrentService({ ...currentService, unit: e.target.value })}
                    data-testid="srn-service-unit"
                  />
                </div>
                <div className="col-span-3 md:col-span-2">
                  <Input
                    type="number"
                    placeholder="Rate"
                    min="0"
                    step="0.01"
                    value={currentService.rate}
                    onChange={(e) => setCurrentService({ ...currentService, rate: e.target.value })}
                    data-testid="srn-service-rate"
                  />
                </div>
                <div className="col-span-1">
                  <Button onClick={handleAddService} size="icon" className="w-full" data-testid="srn-add-service-btn">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Services List */}
              {formData.services.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.services.map((service, index) => (
                      <TableRow key={index}>
                        <TableCell>{service.description}</TableCell>
                        <TableCell className="text-right">{service.quantity}</TableCell>
                        <TableCell>{service.unit || '-'}</TableCell>
                        <TableCell className="text-right">{formatCurrency(service.rate)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(service.amount)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveService(index)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* GST Entries */}
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Receipt className="h-4 w-4" />
                GST Entries
              </h4>
              
              {/* Add GST Form */}
              <div className="flex flex-col md:flex-row gap-2 p-3 bg-muted rounded-lg items-end">
                <div className="w-full md:w-40">
                  <Label className="text-xs">GST %</Label>
                  <Select
                    value={currentGST.percentage.toString()}
                    onValueChange={(value) => setCurrentGST({ ...currentGST, percentage: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="srn-gst-percentage">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5%</SelectItem>
                      <SelectItem value="12">12%</SelectItem>
                      <SelectItem value="18">18% (ITC Eligible)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label className="text-xs">GST Amount (₹)</Label>
                  <Input
                    type="number"
                    placeholder="Amount"
                    min="0"
                    step="0.01"
                    value={currentGST.amount}
                    onChange={(e) => setCurrentGST({ ...currentGST, amount: e.target.value })}
                    data-testid="srn-gst-amount"
                  />
                </div>
                <Button onClick={handleAddGST} data-testid="srn-add-gst-btn">Add GST</Button>
              </div>

              {/* GST List */}
              {formData.gst_entries.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.gst_entries.map((gst, index) => (
                    <Badge key={index} variant="outline" className="gap-2 py-2">
                      GST {gst.percentage}%: {formatCurrency(gst.amount)}
                      {gst.percentage === 18 && <span className="text-green-600">(ITC)</span>}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-4 w-4 ml-1"
                        onClick={() => handleRemoveGST(index)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                data-testid="srn-notes"
              />
            </div>

            {/* Totals */}
            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Service Total:</span>
                <span>{formatCurrency(totals.serviceTotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total GST:</span>
                <span>{formatCurrency(totals.gstTotal)}</span>
              </div>
              {totals.inputCredit > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Input Credit Eligible (18%):</span>
                  <span>{formatCurrency(totals.inputCredit)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-medium border-t pt-2">
                <span>Grand Total:</span>
                <span className="text-primary">{formatCurrency(totals.grandTotal)}</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="srn-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {selectedSRN ? 'Update' : 'Create'} SRN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View SRN Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[1000px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Service Received Note - {selectedSRN?.srn_number}</DialogTitle>
          </DialogHeader>

          {selectedSRN && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 text-sm p-4 md:p-6 bg-muted rounded-lg">
                <div>
                  <span className="text-muted-foreground">PO Number:</span>
                  <p className="font-medium">{selectedSRN.po_number}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Vendor:</span>
                  <p className="font-medium">{selectedSRN.vendor_name || selectedSRN.custom_vendor_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Receipt Date:</span>
                  <p className="font-medium">{formatDate(selectedSRN.receipt_date)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Received By:</span>
                  <p className="font-medium">{selectedSRN.received_by_name}</p>
                </div>
              </div>

              {selectedSRN.bill_number && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Bill Number:</span>
                  <span className="ml-2 font-medium">{selectedSRN.bill_number}</span>
                </div>
              )}

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Service Description</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedSRN.services?.map((service, index) => (
                    <TableRow key={index}>
                      <TableCell>{service.description}</TableCell>
                      <TableCell className="text-right">{service.quantity}</TableCell>
                      <TableCell>{service.unit || '-'}</TableCell>
                      <TableCell className="text-right">{formatCurrency(service.rate)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(service.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="flex justify-end">
                <div className="w-64 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Service Total:</span>
                    <span>{formatCurrency(selectedSRN.total_value)}</span>
                  </div>
                  {selectedSRN.total_gst > 0 && (
                    <>
                      {selectedSRN.gst_5 > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">GST 5%:</span>
                          <span>{formatCurrency(selectedSRN.gst_5)}</span>
                        </div>
                      )}
                      {selectedSRN.gst_12 > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">GST 12%:</span>
                          <span>{formatCurrency(selectedSRN.gst_12)}</span>
                        </div>
                      )}
                      {selectedSRN.gst_18 > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">GST 18%:</span>
                          <span>{formatCurrency(selectedSRN.gst_18)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-medium">
                        <span className="text-muted-foreground">Total GST:</span>
                        <span>{formatCurrency(selectedSRN.total_gst)}</span>
                      </div>
                      {selectedSRN.input_credit_eligible > 0 && (
                        <div className="flex justify-between text-green-600 bg-green-50 p-1 rounded">
                          <span>Input Credit:</span>
                          <span>{formatCurrency(selectedSRN.input_credit_eligible)}</span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex justify-between font-medium text-lg border-t pt-2">
                    <span>Grand Total:</span>
                    <span>{formatCurrency(selectedSRN.grand_total || selectedSRN.total_value)}</span>
                  </div>
                </div>
              </div>

              {selectedSRN.notes && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Notes:</span>
                  <p className="mt-1">{selectedSRN.notes}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge SRNs Confirmation Dialog */}
      <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Merge className="h-5 w-5" />
              Merge Service Received Notes
            </DialogTitle>
            <DialogDescription>
              This will combine {selectedSRNs.length} SRNs into a single record. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="space-y-2 text-sm">
              <p><strong>SRNs to merge:</strong></p>
              <ul className="list-disc list-inside space-y-1">
                {selectedSRNs.map(srnId => {
                  const srn = srns.find(s => s.id === srnId);
                  return srn ? (
                    <li key={srnId} className="text-muted-foreground">
                      {srn.srn_number} - {formatCurrency(srn.grand_total || srn.total_value)}
                    </li>
                  ) : null;
                })}
              </ul>
            </div>
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                <strong>Note:</strong> The oldest SRN will be kept, and services from other SRNs will be merged into it.
                Associated POs may also be merged if they are different.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleMergeSRNs} disabled={merging} data-testid="confirm-merge-btn">
              {merging && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Merge SRNs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceReceivedPage;
